import PopUpNotification, { deliveryTypeIncludesPush } from '@advanza/interfaces/PopUpNotification'
import ProviderNotificationContent, {
    providerNotificationImageUrl,
} from '@advanza/providerNotifications/ProviderNotificationContent'
import { BaseModal, BaseModalHeader, BaseModalMainContent, Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import PushNotificationPreview from 'components/popUpNotifications/PushNotificationPreview'
import { useState } from 'react'

type PreviewTab = 'pop_up' | 'push'

interface Props {
    notification: PopUpNotification
}

/**
 * Shows what the notification will look like, without sending anything.
 *
 * The pop-up half renders the exact component the pros dashboard uses, so the preview cannot drift
 * from production.
 */
const PopUpNotificationPreviewModal = NiceModal.create(({ notification }: Props) => {
    const modal = useModal()
    const hasPush = deliveryTypeIncludesPush(notification.delivery_type)
    const [tab, setTab] = useState<PreviewTab>(hasPush ? 'push' : 'pop_up')

    // An image that has been dropped but not saved yet only exists as a data URL.
    const imageUrl = notification.file?.preview ?? providerNotificationImageUrl(notification)

    return (
        <BaseModal onClose={() => modal.hide()} modalInsideStyle={{ width: '420px' }}>
            <BaseModalHeader>
                <div className="flex items-center justify-between gap-4">
                    <span className="font-bold">Preview</span>
                    {hasPush && (
                        <div className="flex gap-1 rounded-lg bg-neutral-100 p-1">
                            <button
                                type="button"
                                onClick={() => setTab('push')}
                                className={`rounded-md px-3 py-1 text-md ${
                                    tab === 'push'
                                        ? 'bg-white font-semibold shadow-xs'
                                        : 'bg-transparent text-neutral-600'
                                }`}>
                                Push
                            </button>
                            <button
                                type="button"
                                onClick={() => setTab('pop_up')}
                                className={`rounded-md px-3 py-1 text-md ${
                                    tab === 'pop_up'
                                        ? 'bg-white font-semibold shadow-xs'
                                        : 'bg-transparent text-neutral-600'
                                }`}>
                                Pop-up
                            </button>
                        </div>
                    )}
                </div>
            </BaseModalHeader>
            <BaseModalMainContent className="flex flex-col gap-4 px-4 py-6">
                {tab === 'push' ? (
                    <PushNotificationPreview notification={notification} imageUrl={imageUrl} />
                ) : (
                    <>
                        <div className="flex flex-col rounded-xl border border-neutral-200 p-4">
                            <ProviderNotificationContent
                                notification={notification}
                                imageUrl={imageUrl}
                            />
                            {notification.cta_button?.name && (
                                <div className="mt-4">
                                    <Button style={{ width: '100%' }} disabled>
                                        {notification.cta_button.name}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <p className="m-0 text-sm text-neutral-500">
                            Shown once per provider on the pros dashboard, within the activation
                            period.
                        </p>
                    </>
                )}
            </BaseModalMainContent>
        </BaseModal>
    )
})

export default PopUpNotificationPreviewModal
